import { useState, useEffect, useCallback, useRef } from "react";
import { db } from "./firebase";
import { ref, onValue, set, update, runTransaction, push } from "firebase/database";

// ─── Constants ────────────────────────────────────────────────────────────────
const SUITS = ["♠","♥","♦","♣"];
const RANKS = ["2","3","4","5","6","7","8","9","10","J","Q","K","A"];
const RED   = new Set(["♥","♦"]);
const RV    = Object.fromEntries(RANKS.map((r, i) => [r, i + 2]));
const MAX   = 12;
const COOLDOWN_MS  = 10000; // 10 second cooldown between draws
const LONGPRESS_MS = 600;   // hold duration to trigger a draw
const TRAIN_CAR = 1; // hardcoded for now; will be dynamic when multi-game support is added

const EMOJI_CATEGORIES_BOWLING = [
  { label: "🎳 Bowling",  emojis: ["🎳","🏆","🥇","🎯","💥","🔥","⚡","👑","🎉","🃏","🚂","💣","🧨","☠️","💀","🤡","👹","👺","🎰","🍺"] },
  { label: "😈 Savage",   emojis: ["😈","👿","🤬","😡","🤮","🤢","🤧","💩","🖕","🫵","😤","🙄","😒","😑","😶","🫠","🥴","😵","🤯","🤪"] },
  { label: "😏 Snarky",   emojis: ["😏","😼","🥱","😴","😪","🤓","🧐","😬","😅","😂","🤣","💀","😭","😩","😫","🙃","😤","🫤","😐","🗿"] },
  { label: "🍻 Drinking", emojis: ["🍺","🍻","🥂","🍷","🥃","🍸","🍹","🧃","🥴","🤮","🤢","💊","🚬","🎲","🃏","🎰","🤑","💸","💰","🫰"] },
  { label: "💪 Hype",     emojis: ["💪","🤜","🤛","👊","✊","🙌","👏","🫶","🤙","🫡","🫣","🫢","🤫","🤐","🤞","🤘","🖖","🫳","🫴","🤲"] },
  { label: "🔞 Spicy",    emojis: ["🍑","🍆","🌶️","🔥","💦","🫦","👅","🥵","😳","🫣","👀","🕵️","🫠","🥩","🍗","🍖","🤤","😋","🐓","🍌"] },
  { label: "🚂 Gravy",    emojis: ["🚂🍗","🚂","🍗","🥣","🍜","🥘","🍲","🫕","🧆","🥩","🍖","🌿","🍃","🌱","🪴","🍄","🌾","🌵","🪸","🌊"] },
];

const EMOJI_CATEGORIES_PINBALL = [
  { label: "🎯 Pinball",  emojis: ["🎯","🏆","🥇","🥈","🥉","💥","⚡","🔥","👑","🎉","🃏","💣","🧨","☠️","🤖","🛸","🌀","🎰","⭐","🌟"] },
  { label: "😈 Savage",   emojis: ["😈","👿","🤬","😡","🤮","🤢","🤧","💩","🖕","🫵","😤","🙄","😒","😑","😶","🫠","🥴","😵","🤯","🤪"] },
  { label: "😏 Snarky",   emojis: ["😏","😼","🥱","😴","😪","🤓","🧐","😬","😅","😂","🤣","💀","😭","😩","😫","🙃","😤","🫤","😐","🗿"] },
  { label: "🍻 Drinking", emojis: ["🍺","🍻","🥂","🍷","🥃","🍸","🍹","🧃","🥴","🤮","🤢","💊","🚬","🎲","🃏","🎰","🤑","💸","💰","🫰"] },
  { label: "💪 Hype",     emojis: ["💪","🤜","🤛","👊","✊","🙌","👏","🫶","🤙","🫡","🫣","🫢","🤫","🤐","🤞","🤘","🖖","🫳","🫴","🤲"] },
  { label: "🔞 Spicy",    emojis: ["🍑","🍆","🌶️","🔥","💦","🫦","👅","🥵","😳","🫣","👀","🕵️","🫠","🥩","🍗","🍖","🤤","😋","🐓","🍌"] },
  { label: "🚂 Gravy",    emojis: ["🚂🍗","🚂","🍗","🥣","🍜","🥘","🍲","🫕","🧆","🥩","🍖","🌿","🍃","🌱","🪴","🍄","🌾","🌵","🪸","🌊"] },
];


// Forces a re-render every 200ms — used for live cooldown countdowns
function useTick() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 200);
    return () => clearInterval(id);
  }, []);
}

const G = {
  bg:       "linear-gradient(160deg,#0d1b2a,#1a2e44,#0d1b2a)",
  accent:   "#e63946",
  accentLt: "#ff6b6b",
  cream:    "#f1e3c6",
  muted:    "#7a8fa6",
  card:     "rgba(241,227,198,0.07)",
  border:   "rgba(241,227,198,0.2)",
  lane:     "linear-gradient(180deg,#2a1f0e,#3d2e14,#2a1f0e)",
};

// ─── Deck ─────────────────────────────────────────────────────────────────────
function buildDeck(nd = 4, nj = 0) {
  let d = [], id = 0;
  for (let i = 0; i < nd; i++)
    for (const s of SUITS)
      for (const r of RANKS)
        d.push({ id: id++, r, s, j: false });
  for (let i = 0; i < nj; i++) d.push({ id: id++, r: "🃏", s: "", j: true });
  for (let i = d.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [d[i], d[j]] = [d[j], d[i]];
  }
  return d;
}

function shuffleCardBack(deck, card) {
  const pos = Math.floor(Math.random() * (deck.length + 1));
  return [...deck.slice(0, pos), card, ...deck.slice(pos)];
}

// ─── Hand Evaluator ───────────────────────────────────────────────────────────
function getCombos(arr, k) {
  if (k === 0) return [[]];
  if (arr.length < k) return [];
  const [first, ...rest] = arr;
  return [...getCombos(rest, k - 1).map(c => [first, ...c]), ...getCombos(rest, k)];
}

function encRanks(ranks) {
  const padded = [...ranks, 0, 0, 0, 0, 0].slice(0, 5);
  return padded.reduce((acc, r, i) => acc + r * Math.pow(15, 4 - i), 0);
}

function scoreHand(cards, jokers = 0) {
  if (!cards.length) return 0;
  const rv = cards.map(c => RV[c.r] || 0);
  const sv = cards.map(c => c.s);
  const rc = {};
  rv.forEach(r => (rc[r] = (rc[r] || 0) + 1));
  const groups = Object.entries(rc)
    .map(([rank, count]) => ({ rank: Number(rank), count }))
    .sort((a, b) => b.count - a.count || b.rank - a.rank);
  const counts = groups.map(g => g.count);
  const primaryRanks   = groups.filter(g => g.count === counts[0]).map(g => g.rank).sort((a, b) => b - a);
  const secondaryRanks = groups.filter(g => g.count < counts[0]).flatMap(g => Array(g.count).fill(g.rank)).sort((a, b) => b - a);
  const tbRanks = [...primaryRanks, ...secondaryRanks];
  const isFlush = sv.length >= 5 && sv.every(s => s === sv[0]);
  const flushRanks = [...rv].sort((a, b) => b - a);
  const un = [...new Set(rv)].sort((a, b) => a - b);
  let isStraight = false, straightHigh = 0;
  if (un.length >= 5) {
    for (let i = 0; i <= un.length - 5; i++)
      if (un[i + 4] - un[i] === 4) { isStraight = true; straightHigh = Math.max(straightHigh, un[i + 4]); }
    if (un.includes(14)) {
      const wheel = [1, ...un.filter(r => r !== 14)];
      for (let i = 0; i <= wheel.length - 5; i++)
        if (wheel[i + 4] - wheel[i] === 4) { isStraight = true; straightHigh = Math.max(straightHigh, wheel[i + 4]); }
    }
  }
  const top = (counts[0] || 0) + jokers;
  const CAT = 15 ** 5;
  if (isFlush && isStraight) return (rv.includes(14) && straightHigh === 14 ? 9 : 8) * CAT + straightHigh;
  if (top >= 4)                                        return 7 * CAT + encRanks(tbRanks);
  if ((counts[0] >= 3 || top >= 3) && counts[1] >= 2) return 6 * CAT + encRanks(tbRanks);
  if (isFlush)                                         return 5 * CAT + encRanks(flushRanks);
  if (isStraight)                                      return 4 * CAT + straightHigh;
  if (top >= 3)                                        return 3 * CAT + encRanks(tbRanks);
  if (top >= 2 && counts[1] >= 2)                      return 2 * CAT + encRanks(tbRanks);
  if (top >= 2)                                        return 1 * CAT + encRanks(tbRanks);
  return encRanks([...rv].sort((a, b) => b - a));
}

function getBestScore(cards) {
  if (!cards || cards.length < 1) return 0;
  const nj = cards.filter(c => !c.j);
  const jc = cards.filter(c => c.j).length;
  const pool = nj.length >= 5 ? getCombos(nj, 5) : [nj];
  let best = 0;
  for (const combo of pool) { const s = scoreHand(combo, jc); if (s > best) best = s; }
  return best;
}

function scoreToLabel(score, mode = "bowling") {
  const CAT = 15 ** 5;
  const cat = Math.floor(score / CAT);
  if (cat >= 9) return "🏆 Royal Flush";
  if (cat >= 8) return "🎯 Straight Flush";
  if (cat >= 7) return "💪 Four of a Kind";
  if (cat >= 6) return "🏠 Full House";
  if (cat >= 5) return "♦ Flush";
  if (cat >= 4) return "📈 Straight";
  if (cat >= 3) return "3️⃣ Three of a Kind";
  if (cat >= 2) return "↕️ Two Pair";
  if (cat >= 1) return "1️⃣ One Pair";
  if (score > 0) return mode === "pinball" ? "🎯 High Card" : "🚂 High Card";
  return null;
}

function evalHand(cards, mode = "bowling") {
  if (!cards || cards.length === 0) return null;
  return scoreToLabel(getBestScore(cards), mode);
}

function getHighHandPlayerIds(players) {
  const entries = Object.values(players || {}).filter(p => p.hand && p.hand.length >= 1);
  if (!entries.length) return [];
  let best = 0;
  entries.forEach(p => { const s = getBestScore(p.hand); if (s > best) best = s; });
  return entries.filter(p => getBestScore(p.hand) === best).map(p => p.id);
}

// ─── Pip layout ───────────────────────────────────────────────────────────────
const PIP_POSITIONS = {
  "2":  [[50,20],[50,80]],
  "3":  [[50,15],[50,50],[50,85]],
  "4":  [[25,20],[75,20],[25,80],[75,80]],
  "5":  [[25,20],[75,20],[50,50],[25,80],[75,80]],
  "6":  [[25,18],[75,18],[25,50],[75,50],[25,82],[75,82]],
  "7":  [[25,18],[75,18],[50,33],[25,52],[75,52],[25,82],[75,82]],
  "8":  [[25,18],[75,18],[50,31],[25,50],[75,50],[50,69],[25,82],[75,82]],
  "9":  [[25,16],[75,16],[25,37],[75,37],[50,50],[25,63],[75,63],[25,84],[75,84]],
  "10": [[25,14],[75,14],[50,27],[25,41],[75,41],[25,59],[75,59],[50,73],[25,86],[75,86]],
};
const FACE_GLYPHS = { J:"J", Q:"Q", K:"K" };

// ─── Card Component ───────────────────────────────────────────────────────────
// ─── Long Press Button ────────────────────────────────────────────────────────
// Fires onTrigger only after the user holds for LONGPRESS_MS.
// Shows a radial fill animation so the player can see progress.
function LongPressButton({ onTrigger, disabled, children, style = {} }) {
  const timerRef    = useRef(null);
  const startRef    = useRef(null);
  const [progress, setProgress] = useState(0); // 0–100
  const animRef     = useRef(null);

  const startPress = () => {
    if (disabled) return;
    startRef.current = Date.now();
    setProgress(0);
    // Animate progress bar
    const tick = () => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.min(100, (elapsed / LONGPRESS_MS) * 100);
      setProgress(pct);
      if (pct < 100) {
        animRef.current = requestAnimationFrame(tick);
      }
    };
    animRef.current = requestAnimationFrame(tick);
    timerRef.current = setTimeout(() => {
      onTrigger();
      setProgress(0);
    }, LONGPRESS_MS);
  };

  const cancelPress = () => {
    clearTimeout(timerRef.current);
    cancelAnimationFrame(animRef.current);
    setProgress(0);
  };

  return (
    <div
      onMouseDown={startPress}
      onMouseUp={cancelPress}
      onMouseLeave={cancelPress}
      onTouchStart={e => { e.preventDefault(); startPress(); }}
      onTouchEnd={cancelPress}
      onTouchCancel={cancelPress}
      style={{
        position: "relative", overflow: "hidden",
        borderRadius: 14, cursor: disabled ? "not-allowed" : "pointer",
        userSelect: "none", touchAction: "none",
        ...style,
      }}
    >
      {/* Fill overlay showing hold progress */}
      {progress > 0 && (
        <div style={{
          position: "absolute", inset: 0, borderRadius: 14,
          background: "rgba(255,255,255,0.25)",
          clipPath: `inset(0 ${100 - progress}% 0 0)`,
          transition: "none", pointerEvents: "none",
        }} />
      )}
      {children}
    </div>
  );
}

function Card({ card, sm = false }) {
  if (!card) return null;
  const isRed = RED.has(card.s);
  const col   = isRed ? "#dc2626" : "#1c1917";
  const w = sm ? 50 : 78, h = sm ? 70 : 112;
  const pad = sm ? 3 : 5;

  if (card.j) {
    return (
      <div style={{ width: w, height: h, borderRadius: 6, background: "linear-gradient(135deg,#2d1b69,#6b21a8)", border: "2px solid #a855f7", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 3px 8px rgba(0,0,0,0.4)", flexShrink: 0, userSelect: "none" }}>
        <span style={{ fontSize: sm ? 24 : 38 }}>🃏</span>
      </div>
    );
  }

  const pips   = PIP_POSITIONS[card.r];
  const isFace = FACE_GLYPHS[card.r] !== undefined;
  const isAce  = card.r === "A";

  return (
    <div style={{ width: w, height: h, borderRadius: 6, background: "#fefce8", border: `2px solid ${col}`, position: "relative", boxShadow: "0 3px 8px rgba(0,0,0,0.4)", flexShrink: 0, userSelect: "none", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: pad, left: pad, lineHeight: 1, textAlign: "center" }}>
        <div style={{ fontSize: sm ? 9 : 13, fontWeight: 900, color: col, lineHeight: 1 }}>{card.r}</div>
        <div style={{ fontSize: sm ? 8 : 11, color: col, lineHeight: 1 }}>{card.s}</div>
      </div>
      <div style={{ position: "absolute", bottom: pad, right: pad, lineHeight: 1, textAlign: "center", transform: "rotate(180deg)" }}>
        <div style={{ fontSize: sm ? 9 : 13, fontWeight: 900, color: col, lineHeight: 1 }}>{card.r}</div>
        <div style={{ fontSize: sm ? 8 : 11, color: col, lineHeight: 1 }}>{card.s}</div>
      </div>
      {isAce && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: sm ? 22 : 36, color: col, lineHeight: 1 }}>{card.s}</span>
        </div>
      )}
      {isFace && (
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: sm ? 1 : 2 }}>
          <span style={{ fontSize: sm ? 18 : 28, fontWeight: 900, color: col, fontFamily: "Georgia,serif", lineHeight: 1 }}>{FACE_GLYPHS[card.r]}</span>
          <span style={{ fontSize: sm ? 12 : 18, color: col, lineHeight: 1 }}>{card.s}</span>
        </div>
      )}
      {pips && (() => {
        const pipSize = sm ? 9 : 14;
        const areaTop = sm ? 16 : 22;
        const areaH   = h - areaTop * 2;
        const areaLeft = sm ? 6 : 8;
        const areaW   = w - areaLeft * 2;
        return pips.map(([xPct, yPct], i) => {
          const x    = areaLeft + (areaW  * xPct / 100) - pipSize / 2;
          const y    = areaTop  + (areaH  * yPct / 100) - pipSize / 2;
          const flip = yPct > 50 && !isRed;
          return <span key={i} style={{ position: "absolute", left: x, top: y, fontSize: pipSize, color: col, lineHeight: 1, display: "block", transform: flip ? "rotate(180deg)" : "none", userSelect: "none" }}>{card.s}</span>;
        });
      })()}
    </div>
  );
}

const B = (extra = {}) => ({
  padding: "10px 18px", borderRadius: 8,
  border: "1px solid rgba(241,227,198,0.2)",
  background: "linear-gradient(135deg,rgba(241,227,198,0.1),rgba(241,227,198,0.04))",
  color: "#f1e3c6", fontWeight: 700, fontSize: 13,
  cursor: "pointer", fontFamily: "'Georgia',serif",
  ...extra,
});

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  // Firebase game state (synced in real-time across all devices)
  const [gs, setGs]           = useState(null);   // full game state from Firebase
  const [loading, setLoading] = useState(true);

  // Role selection — persisted for the session so refresh keeps you in your role
  const [role, setRole] = useState(
    () => sessionStorage.getItem("gravy_role") || null  // null | "admin" | "player"
  );

  const chooseRole = r => {
    sessionStorage.setItem("gravy_role", r);
    setRole(r);
    // Drop admin auth if switching away from admin
    if (r !== "admin") {
      sessionStorage.removeItem("gravy_admin_auth");
      setAdminAuthed(false);
    }
    setView(r === "admin" ? "admin" : "join");
  };

  const switchRole = () => {
    sessionStorage.removeItem("gravy_role");
    sessionStorage.removeItem("gravy_admin_auth");
    setRole(null);
    setAdminAuthed(false);
    setView("admin");
  };

  // Admin auth — checked against env var, persisted for the browser session
  const [adminAuthed, setAdminAuthed] = useState(
    () => sessionStorage.getItem("gravy_admin_auth") === "true"
  );
  const [adminPassInput, setAdminPassInput] = useState("");
  const [adminPassErr, setAdminPassErr]     = useState("");

  const handleAdminLogin = () => {
    const correct = process.env.REACT_APP_ADMIN_PASSWORD;
    if (adminPassInput === correct) {
      sessionStorage.setItem("gravy_admin_auth", "true");
      setAdminAuthed(true);
      setAdminPassErr("");
    } else {
      setAdminPassErr("Incorrect password");
      setAdminPassInput("");
    }
  };

  const handleAdminLogout = () => {
    sessionStorage.removeItem("gravy_admin_auth");
    sessionStorage.removeItem("gravy_role");
    setAdminAuthed(false);
    setRole(null);
    setAdminPassInput("");
  };

  // Local UI-only state (not synced — each device manages its own view)
  const [nd, setNd]                   = useState(4);
  const [nj, setNj]                   = useState(8);
  const [pot, setPot]                 = useState("");
  const [gameMode, setGameMode]       = useState("bowling"); // "bowling" | "pinball"
  const [view, setView]               = useState(role === "admin" ? "admin" : "join");
  const [joinName, setJoinName]       = useState("");
  const [joinErr, setJoinErr]         = useState("");
  // Track which player ID belongs to this device (players only)
  const [myPlayerId, setMyPlayerId]   = useState(
    () => sessionStorage.getItem("gravy_player_id") || null
  );
  const [flash, setFlash]             = useState(null);
  const [confirmReturn, setConfirmReturn]       = useState(null);
  const [confirmLogoutAll, setConfirmLogoutAll] = useState(false);
  const prevHighIdsRef                = useRef([]);
  const prevNewGameAlertRef           = useRef(null);
  const prevSessionResetRef           = useRef(null);

  const gameRef = ref(db, `games/${TRAIN_CAR}`);
  const chatRef = ref(db, `games/${TRAIN_CAR}/chat`);

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatMsg, setChatMsg]           = useState("");
  const [chatOpen, setChatOpen]         = useState(true);
  const [unreadCount, setUnreadCount]   = useState(0);
  const [emojiOpen, setEmojiOpen]       = useState(false);
  const [emojiCat, setEmojiCat]         = useState(0);
  const seenCountRef                    = useRef(0);
  const chatBottomRef                   = useRef(null);
  const chatInputRef                    = useRef(null);

  // ── Subscribe to Firebase ──────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onValue(gameRef, snap => {
      const data = snap.val();
      setLoading(false);
      if (!data) {
        setGs(null);
        // Game was reset by admin — clear this device's player identity so they can rejoin
        sessionStorage.removeItem("gravy_player_id");
        setMyPlayerId(null);
        setView(role === "admin" ? "admin" : "join");
        return;
      }

      // Normalize Firebase arrays/objects back to plain arrays where needed
      const normalized = {
        ...data,
        deck:    data.deck    ? Object.values(data.deck)    : [],
        players: data.players || {},
        pending: data.pending || {},
      };
      // Normalize each player's hand
      Object.keys(normalized.players).forEach(pid => {
        const p = normalized.players[pid];
        normalized.players[pid] = {
          ...p,
          hand: p.hand ? Object.values(p.hand) : [],
        };
      });

      // Detect high hand changes and toast admin
      const newHighIds = getHighHandPlayerIds(normalized.players);
      const prevKey    = [...prevHighIdsRef.current].sort().join(",");
      const newKey     = [...newHighIds].sort().join(",");
      if (newKey !== prevKey && newHighIds.length > 0 && normalized.phase === "playing") {
        const names = newHighIds.map(id => normalized.players[id]?.name).filter(Boolean).join(" & ");
        const label = scoreToLabel(getBestScore(normalized.players[newHighIds[0]]?.hand || []), mode);
        toast(`👑 High Hand: ${names} — ${label}`);
      }
      prevHighIdsRef.current = newHighIds;

      // Detect "New Game — Same Players" and toast all devices
      // Skip on initial load by only firing when we already have a known alert value
      const alertTs = normalized.newGameAlert || null;
      if (
        alertTs &&
        prevNewGameAlertRef.current !== null &&
        alertTs !== prevNewGameAlertRef.current
      ) {
        toast("🚂 New game starting — good luck!", true);
      }
      // On very first load, just record whatever value is there without toasting
      prevNewGameAlertRef.current = prevNewGameAlertRef.current === null
        ? (alertTs ?? 0)
        : (alertTs ?? prevNewGameAlertRef.current);

      // Detect sessionReset — wipe local session and send everyone back to role selection
      const resetTs = normalized.sessionReset || null;
      if (
        resetTs &&
        prevSessionResetRef.current !== null &&
        resetTs !== prevSessionResetRef.current
      ) {
        sessionStorage.removeItem("gravy_admin_auth");
        sessionStorage.removeItem("gravy_role");
        sessionStorage.removeItem("gravy_player_id");
        // Use a small delay so the state update reads cleanly
        setTimeout(() => {
          setAdminAuthed(false);
          setRole(null);
          setMyPlayerId(null);
          setView("admin");
        }, 100);
      }
      prevSessionResetRef.current = prevSessionResetRef.current === null
        ? (resetTs ?? 0)
        : (resetTs ?? prevSessionResetRef.current);

      setGs(normalized);
    });
    return () => unsub();
  }, []);

  // Subscribe to chat messages
  useEffect(() => {
    const unsub = onValue(chatRef, snap => {
      const data = snap.val();
      if (!data) { setChatMessages([]); seenCountRef.current = 0; return; }
      const msgs = Object.values(data).sort((a, b) => a.ts - b.ts);
      setChatMessages(msgs);
      setChatOpen(prev => {
        if (!prev) {
          // Chat is closed — increment unread for any new messages
          const newCount = msgs.length - seenCountRef.current;
          if (newCount > 0) setUnreadCount(u => u + newCount);
        } else {
          // Chat is open — mark all as seen, scroll to bottom
          setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
        }
        seenCountRef.current = msgs.length;
        return prev;
      });
    });
    return () => unsub();
  }, []);

  // useTick for cooldown countdown re-renders
  useTick();

  const sendChat = () => {
    const text = chatMsg.trim();
    if (!text) return;
    const senderName = role === "admin" ? "Admin 🔐" : (players[myPlayerId]?.name || "Unknown");
    push(chatRef, { name: senderName, text, ts: Date.now() });
    setChatMsg("");
  };

  // Insert emoji at the cursor position in the chat input
  const insertEmoji = (emoji) => {
    const input = chatInputRef.current;
    if (!input) { setChatMsg(prev => prev + emoji); return; }
    const start = input.selectionStart;
    const end   = input.selectionEnd;
    const next  = chatMsg.slice(0, start) + emoji + chatMsg.slice(end);
    setChatMsg(next);
    // Restore cursor position after React re-render
    requestAnimationFrame(() => {
      input.focus();
      const pos = start + emoji.length;
      input.setSelectionRange(pos, pos);
    });
  };

  // Auto-switch player to their own tab once approved, when game ends, or on rematch
  useEffect(() => {
    if (role !== "player" || !myPlayerId || !gs) return;
    const isApproved = !!gs.players?.[myPlayerId];
    // Move to their tab when approved, when game ends, or when a rematch starts
    if (isApproved && (view === "join" || (gs.phase === "playing" && view !== myPlayerId))) {
      setView(myPlayerId);
    }
  }, [gs, myPlayerId, role, view]);

  const toast = (msg, ok = true) => {
    setFlash({ msg, ok });
    setTimeout(() => setFlash(null), 2800);
  };

  // ── Firebase write helpers ─────────────────────────────────────────────────
  const fbSet    = (path, value) => set(ref(db, `games/${TRAIN_CAR}/${path}`), value);
  const fbUpdate = (updates)     => update(gameRef, updates);

  // ── Game actions ───────────────────────────────────────────────────────────
  const startGame = () => {
    const newState = {
      phase:      "playing",
      numDecks:   nd,
      numJokers:  nj,
      pot:        pot ? Number(pot) : 0,
      gameMode:   gameMode,
      gameLocked: false,
      deck:       buildDeck(nd, nj),
      players:    {},
      pending:    {},
    };
    set(gameRef, newState);
    setView("admin");
    prevHighIdsRef.current = [];
  };

  const reqJoin = () => {
    if (!joinName.trim()) { setJoinErr("Enter your name"); return; }
    if (gs?.gameLocked)   { setJoinErr("The game is locked — no new players can join."); return; }

    // If we have a saved player ID but it no longer exists in the current game
    // (admin started a new game), clear it so the player can rejoin fresh
    if (myPlayerId) {
      const stillInGame = gs?.players?.[myPlayerId] || gs?.pending?.[myPlayerId];
      if (!stillInGame) {
        sessionStorage.removeItem("gravy_player_id");
        setMyPlayerId(null);
      } else {
        setJoinErr("You have already joined."); return;
      }
    }

    const allNames = [
      ...Object.values(gs?.players || {}).map(p => p.name.toLowerCase()),
      ...Object.values(gs?.pending || {}).map(p => p.name.toLowerCase()),
    ];
    if (allNames.includes(joinName.trim().toLowerCase())) { setJoinErr("Name already taken"); return; }
    const pid = `p_${Date.now()}`;
    sessionStorage.setItem("gravy_player_id", pid);
    setMyPlayerId(pid);
    fbSet(`pending/${pid}`, { id: pid, name: joinName.trim() });
    toast(`"${joinName.trim()}" is waiting for approval`);
    setJoinName(""); setJoinErr("");
  };

  const approve = pid => {
    const p = gs?.pending?.[pid];
    if (!p) return;
    fbUpdate({
      [`players/${pid}`]: { id: pid, name: p.name, hand: [], drawn: 0 },
      [`pending/${pid}`]: null,
    });
    toast(`${p.name} approved!`);
  };

  const deny = pid => {
    const p = gs?.pending?.[pid];
    fbSet(`pending/${pid}`, null);
    if (p) toast(`${p.name} denied`, false);
  };

  const boot = pid => {
    const p = gs?.players?.[pid];
    fbSet(`players/${pid}`, null);
    if (view === pid) setView("admin");
    if (p) toast(`${p.name} removed`, false);
  };

  // runTransaction ensures two devices can never draw the same card simultaneously
  const drawCard = useCallback(pid => {
    runTransaction(gameRef, current => {
      if (!current) return current;
      const deck    = current.deck    ? Object.values(current.deck)    : [];
      const players = current.players || {};
      const player  = players[pid];
      if (!player || !deck.length) return current;

      const hand = player.hand ? Object.values(player.hand) : [];
      if ((player.drawn || 0) >= MAX) return current;

      // Enforce cooldown server-side for bowling only — pinball needs rapid draws
      const now = Date.now();
      if (current.gameMode !== "pinball" && player.lastDrawAt && (now - player.lastDrawAt) < COOLDOWN_MS) return current;

      const [card, ...rest] = deck;
      return {
        ...current,
        deck: rest,
        players: {
          ...players,
          [pid]: { ...player, hand: [...hand, card], drawn: (player.drawn || 0) + 1, lastDrawAt: now },
        },
      };
    }).catch(err => console.error("Draw transaction failed:", err));
  }, []);

  const promptReturn = (pid, cardId) => {
    const player = gs?.players?.[pid];
    const card   = player?.hand?.find(c => c.id === cardId);
    if (!card) return;
    const label = card.j ? "🃏 Joker" : `${card.r}${card.s}`;
    setConfirmReturn({ pid, cardId, label, playerName: player.name });
  };

  const confirmReturnCard = () => {
    if (!confirmReturn) return;
    const { pid, cardId } = confirmReturn;
    setConfirmReturn(null);
    runTransaction(gameRef, current => {
      if (!current) return current;
      const players = current.players || {};
      const player  = players[pid];
      if (!player) return current;
      const hand = player.hand ? Object.values(player.hand) : [];
      const card = hand.find(c => c.id === cardId);
      if (!card) return current;
      const newHand = hand.filter(c => c.id !== cardId);
      const deck    = current.deck ? Object.values(current.deck) : [];
      const newDeck = shuffleCardBack(deck, card);
      return {
        ...current,
        deck: newDeck,
        players: {
          ...players,
          [pid]: { ...player, hand: newHand, drawn: Math.max(0, (player.drawn || 0) - 1) },
        },
      };
    }).then(() => toast(`Card returned to deck`))
      .catch(err => console.error("Return transaction failed:", err));
  };

  const lockGame  = () => fbSet("gameLocked", true);
  const endGame   = () => fbSet("phase", "ended");

  // Writes sessionReset timestamp AND wipes all player/game data from Firebase
  // so names are freed up and everyone starts completely fresh next session
  const logoutAll = () => {
    // Wipe the entire game node and replace with just the reset signal
    set(gameRef, {
      sessionReset: Date.now(),
    });
    // Also clear chat history so last week's messages don't carry over
    set(chatRef, null);
    setConfirmLogoutAll(false);
  };
  // Full reset — clears everything, admin goes back to Game Setup
  const reset = () => {
    set(gameRef, null);
    setView("admin");
    setMyPlayerId(null);
    sessionStorage.removeItem("gravy_player_id");
    prevHighIdsRef.current = [];
  };

  // Soft reset — same players, fresh deck, unlocked so new players can join
  const rematchGame = () => {
    const existingPlayers = gs?.players || {};
    const resetPlayers = Object.fromEntries(
      Object.entries(existingPlayers).map(([pid, p]) => [
        pid,
        { id: p.id, name: p.name, hand: [], drawn: 0 },
      ])
    );
    const newState = {
      phase:      "playing",
      numDecks:   gs?.numDecks  || 4,
      numJokers:  gs?.numJokers || 0,
      gameLocked: false,   // unlock so new players can join the next game
      deck:       buildDeck(gs?.numDecks || 4, gs?.numJokers || 0),
      players:    resetPlayers,
      pending:    {},
      newGameAlert: Date.now(), // triggers player notification
    };
    set(gameRef, newState);
    prevHighIdsRef.current = [];
  };

  // ── Derived state ──────────────────────────────────────────────────────────
  const phase      = gs?.phase      || "setup";
  const gameLocked = gs?.gameLocked || false;
  const deck       = gs?.deck       || [];
  const players    = gs?.players    || {};
  const pending    = gs?.pending    || {};
  const gamePot    = gs?.pot        || 0;
  const mode       = gs?.gameMode   || "bowling"; // "bowling" | "pinball"
  const pl         = Object.values(players);
  const pend       = Object.values(pending);
  const highIds    = getHighHandPlayerIds(players);

  // ── Loading screen ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: G.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: G.muted }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🚂</div>
          <p style={{ fontFamily: "Georgia,serif", fontSize: 14 }}>Boarding the train…</p>
        </div>
      </div>
    );
  }

  // ── Role selection screen ─────────────────────────────────────────────────
  if (!role) {
    return (
      <div style={{ minHeight: "100vh", background: G.bg, fontFamily: "'Georgia',serif", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ position: "fixed", inset: 0, background: G.lane, opacity: 0.08, pointerEvents: "none" }} />
        <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 400, textAlign: "center" }}>
          {/* Logo */}
          <div style={{ margin: "0 auto 10px", width: 110, height: 70 }}>
            <svg viewBox="0 0 120 80" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%" }}>
              <circle cx="30" cy="12" r="5" fill="rgba(241,227,198,0.25)" />
              <circle cx="40" cy="7"  r="4" fill="rgba(241,227,198,0.18)" />
              <circle cx="50" cy="10" r="6" fill="rgba(241,227,198,0.15)" />
              <ellipse cx="60" cy="30" rx="42" ry="14" fill="#3b1f0a" />
              <ellipse cx="60" cy="27" rx="42" ry="10" fill="#6b3a10" />
              <ellipse cx="52" cy="25" rx="22" ry="5"  fill="#8b5014" opacity="0.6" />
              <path d="M18 35 Q16 42 18 46" stroke="#6b3a10" strokeWidth="3" fill="none" strokeLinecap="round"/>
              <path d="M102 35 Q104 42 102 46" stroke="#6b3a10" strokeWidth="3" fill="none" strokeLinecap="round"/>
              <rect x="14" y="34" width="92" height="22" rx="4" fill="#8b5014" />
              <rect x="14" y="34" width="92" height="8"  rx="4" fill="#a0621a" />
              <rect x="26" y="20" width="10" height="16" rx="2" fill="#5a3209" />
              <rect x="23" y="18" width="16" height="5"  rx="2" fill="#3b1f0a" />
              <rect x="88" y="37" width="14" height="12" rx="2" fill="#fde68a" opacity="0.85" />
              {[30, 60, 90].map((cx, i) => (
                <g key={i}>
                  <circle cx={cx} cy="56" r="12" fill="#c8851a" />
                  <circle cx={cx} cy="56" r="8"  fill="#e0a840" />
                  {[[0,-5],[0,5],[-4,-2],[4,-2],[-4,2],[4,2]].map(([dx,dy], j) => (
                    <circle key={j} cx={cx+dx} cy={56+dy} r="0.9" fill="#a0621a" />
                  ))}
                  <circle cx={cx} cy="56" r="2.5" fill="#7a4a0a" />
                </g>
              ))}
              <rect x="8" y="68" width="104" height="3" rx="1.5" fill="#4a3010" />
            </svg>
          </div>
          <div style={{ fontSize: 11, letterSpacing: 6, color: G.muted, textTransform: "uppercase", marginBottom: 6 }}>Est. 2026 · Train Car #{TRAIN_CAR}</div>
          <h1 style={{ margin: "0 0 4px", fontSize: 26, color: G.cream, fontStyle: "italic", letterSpacing: 1, textShadow: "0 0 40px rgba(230,57,70,0.4)", lineHeight: 1.2 }}>
            Ridin' the Gravy Train Poker
          </h1>
          <div style={{ width: 180, height: 1, background: `linear-gradient(90deg,transparent,${G.accent},transparent)`, margin: "14px auto 20px" }} />
          {/* Prominent Train Car badge */}
          <div style={{ display: "inline-block", background: `linear-gradient(135deg,${G.accent},#c1121f)`, borderRadius: 12, padding: "10px 28px", marginBottom: 24 }}>
            <span style={{ color: "#fff", fontSize: 20, fontWeight: 800, fontStyle: "italic", letterSpacing: 1 }}>
              🚂 Gravy Train Car #{TRAIN_CAR}
            </span>
          </div>
          <p style={{ color: G.muted, fontSize: 14, marginBottom: 20 }}>Who are you?</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <button onClick={() => chooseRole("player")}
              style={B({ width: "100%", padding: "18px 0", fontSize: 16, background: `linear-gradient(135deg,${G.accent},#c1121f)`, color: "#fff", border: "none", borderRadius: 12, letterSpacing: 0.5 })}>
              🚂 I'm a Player
            </button>
            <button onClick={() => chooseRole("admin")}
              style={B({ width: "100%", padding: "18px 0", fontSize: 16, borderRadius: 12, letterSpacing: 0.5 })}>
              ⚙️ I'm the Admin
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: G.bg, fontFamily: "'Georgia',serif", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "fixed", inset: 0, background: G.lane, opacity: 0.08, pointerEvents: "none", zIndex: 0 }} />
      {["🚂","🍗","🚂","🍗"].map((s, i) => (
        <div key={i} style={{ position: "fixed", fontSize: 120, opacity: 0.04, pointerEvents: "none", zIndex: 0, top: i < 2 ? -20 : "auto", bottom: i >= 2 ? -20 : "auto", left: i % 2 === 0 ? -20 : "auto", right: i % 2 === 1 ? -20 : "auto" }}>{s}</div>
      ))}

      {flash && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", background: flash.ok ? "rgba(22,163,74,0.95)" : "rgba(220,38,38,0.95)", color: "#fff", padding: "9px 22px", borderRadius: 30, fontWeight: 700, fontSize: 13, zIndex: 100, boxShadow: "0 4px 20px rgba(0,0,0,0.5)", whiteSpace: "nowrap", animation: "fi 0.2s ease" }}>{flash.msg}</div>
      )}

      {/* ── Persistent admin toolbar — always visible when admin is logged in ── */}
      {role === "admin" && adminAuthed && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 140, background: "rgba(8,14,24,0.97)", borderTop: `1px solid ${G.border}`, padding: "10px 16px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", boxShadow: "0 -2px 16px rgba(0,0,0,0.4)" }}>
          <span style={{ color: G.muted, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", marginRight: 4 }}>⚙️ Admin</span>
          <button onClick={handleAdminLogout} style={B({ color: G.muted, fontSize: 12, padding: "6px 12px" })}>🚪 Log Out</button>
          <button onClick={switchRole} style={B({ color: G.muted, fontSize: 12, padding: "6px 12px" })}>← Switch Role</button>
          <button
            onClick={() => setConfirmLogoutAll(true)}
            style={B({ color: "#f87171", fontSize: 12, padding: "6px 14px", background: "rgba(220,38,38,0.12)", border: "1px solid rgba(248,113,113,0.4)" })}>
            🔴 Log Out Everyone
          </button>
        </div>
      )}

      <div style={{ position: "relative", zIndex: 1, maxWidth: 860, margin: "0 auto", padding: "0 0 60px", paddingTop: (phase === "playing" || phase === "ended") ? (chatOpen ? (emojiOpen ? 520 : 360) : 46) : 0, paddingBottom: role === "admin" && adminAuthed ? 80 : 60, transition: "padding-top 0.25s ease" }}>

        {/* ── Header ── */}
        <div style={{ textAlign: "center", padding: "28px 20px 0" }}>
          <div style={{ fontSize: 11, letterSpacing: 6, color: G.muted, textTransform: "uppercase", marginBottom: 6 }}>Est. 2026 &nbsp;·&nbsp; Train Car #{TRAIN_CAR}{gamePot > 0 ? ` · $${gamePot.toLocaleString()}` : ""}</div>
          <div style={{ margin: "0 auto 8px", width: 110, height: 70 }}>
            <svg viewBox="0 0 120 80" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%" }}>
              <circle cx="30" cy="12" r="5" fill="rgba(241,227,198,0.25)" />
              <circle cx="40" cy="7"  r="4" fill="rgba(241,227,198,0.18)" />
              <circle cx="50" cy="10" r="6" fill="rgba(241,227,198,0.15)" />
              <ellipse cx="60" cy="30" rx="42" ry="14" fill="#3b1f0a" />
              <ellipse cx="60" cy="27" rx="42" ry="10" fill="#6b3a10" />
              <ellipse cx="52" cy="25" rx="22" ry="5"  fill="#8b5014" opacity="0.6" />
              <path d="M18 35 Q16 42 18 46" stroke="#6b3a10" strokeWidth="3" fill="none" strokeLinecap="round"/>
              <path d="M102 35 Q104 42 102 46" stroke="#6b3a10" strokeWidth="3" fill="none" strokeLinecap="round"/>
              <rect x="14" y="34" width="92" height="22" rx="4" fill="#8b5014" />
              <rect x="14" y="34" width="92" height="8"  rx="4" fill="#a0621a" />
              <rect x="26" y="20" width="10" height="16" rx="2" fill="#5a3209" />
              <rect x="23" y="18" width="16" height="5"  rx="2" fill="#3b1f0a" />
              <rect x="88" y="37" width="14" height="12" rx="2" fill="#fde68a" opacity="0.85" />
              {[30, 60, 90].map((cx, i) => (
                <g key={i}>
                  <circle cx={cx} cy="56" r="12" fill="#c8851a" />
                  <circle cx={cx} cy="56" r="12" fill="none" stroke="#a0621a" strokeWidth="1.5" />
                  <circle cx={cx} cy="56" r="8"  fill="#e0a840" />
                  <circle cx={cx} cy="56" r="8"  fill="none" stroke="#c8851a" strokeWidth="1" />
                  {[[0,-5],[0,5],[-4,-2],[4,-2],[-4,2],[4,2]].map(([dx,dy], j) => (
                    <circle key={j} cx={cx+dx} cy={56+dy} r="0.9" fill="#a0621a" />
                  ))}
                  <circle cx={cx} cy="56" r="2.5" fill="#7a4a0a" />
                </g>
              ))}
              <rect x="8" y="68" width="104" height="3" rx="1.5" fill="#4a3010" />
              <rect x="8" y="71" width="104" height="2" rx="1"   fill="#3a2008" opacity="0.5"/>
            </svg>
          </div>
          <h1 style={{ margin: 0, fontSize: 26, color: G.cream, fontStyle: "italic", letterSpacing: 1, textShadow: "0 0 40px rgba(230,57,70,0.4)", lineHeight: 1.2 }}>
            Ridin' the Gravy Train Poker
          </h1>
          <div style={{ display: "inline-block", margin: "10px auto 0", background: G.accent, borderRadius: 20, padding: "5px 18px" }}>
            {mode === "bowling" ? (
              <span style={{ color: "#fff", fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }}>
                🎳 Strike = 1 card &nbsp;·&nbsp; 9 spare or split = 1 card
              </span>
            ) : (
              <div style={{ color: "#fff", fontSize: 12, fontWeight: 700, letterSpacing: 0.5, padding: "2px 0" }}>
                <div style={{ marginBottom: 3 }}>🎯 Pinball card rules</div>
                <div style={{ display: "flex", gap: 14, fontSize: 11, fontWeight: 400, opacity: 0.92 }}>
                  <span>2 players: 1st = 1</span>
                  <span>3 players: 1st = 2, 2nd = 1</span>
                  <span>4 players: 1st = 3, 2nd = 2, 3rd = 1</span>
                </div>
              </div>
            )}
          </div>
          <div style={{ width: 180, height: 1, background: `linear-gradient(90deg,transparent,${G.accent},transparent)`, margin: "12px auto" }} />
          {phase !== "setup" && (
            <div style={{ display: "flex", gap: 20, justifyContent: "center", fontSize: 12, color: G.muted, flexWrap: "wrap", paddingBottom: 16 }}>
              <span>🃏 {deck.length} cards left</span>
              <span>👥 {pl.length} players</span>
              <span>🎴 {gs?.numDecks ?? 4} deck{(gs?.numDecks ?? 4) > 1 ? "s" : ""}{(gs?.numJokers ?? 0) > 0 ? ` +${gs.numJokers}J` : ""}</span>
              {gamePot > 0 && <span style={{ color: "#4ade80", fontWeight: 700 }}>💰 ${gamePot.toLocaleString()}</span>}
              {highIds.length > 0 && <span style={{ color: G.accent }}>👑 {highIds.map(id => players[id]?.name).join(" & ")}</span>}
            </div>
          )}
        </div>

        {/* ── SETUP — admin only ── */}
        {phase === "setup" && role === "admin" && adminAuthed && (
          <div style={{ padding: "20px 20px 0" }}>
            <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 16, padding: 32, maxWidth: 400, margin: "0 auto" }}>
              <h2 style={{ color: G.cream, margin: "0 0 24px", textAlign: "center", fontSize: 19 }}>Game Setup</h2>

              {/* Game mode toggle */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ color: G.muted, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>Game Mode</div>
                <div style={{ display: "flex", gap: 0, background: "rgba(0,0,0,0.3)", borderRadius: 10, padding: 3, border: `1px solid ${G.border}` }}>
                  {[{ val: "bowling", label: "🎳 Bowling" }, { val: "pinball", label: "🎯 Pinball" }].map(opt => (
                    <button key={opt.val} onClick={() => { setGameMode(opt.val); setEmojiCat(0); }}
                      style={{ flex: 1, padding: "11px 0", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "'Georgia',serif", fontWeight: 700, fontSize: 15, transition: "all 0.2s",
                        background: gameMode === opt.val ? `linear-gradient(135deg,${G.accent},#c1121f)` : "transparent",
                        color: gameMode === opt.val ? "#fff" : G.muted,
                      }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {[
                { label: "Decks",  val: nd, set: setNd, min: 1, max: 10, note: `${nd * 52 + nj} total cards` },
                { label: "Jokers", val: nj, set: setNj, min: 0, max: 20, note: `${nj} wild${nj !== 1 ? "s" : ""}` },
              ].map(({ label, val, set, min, max, note }) => (
                <div key={label} style={{ marginBottom: 20 }}>
                  <div style={{ color: G.muted, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <button onClick={() => set(v => Math.max(min, v - 1))} style={B({ width: 48, height: 48, padding: 0, fontSize: 26, display: "flex", alignItems: "center", justifyContent: "center" })}>−</button>
                    <div style={{ flex: 1, textAlign: "center", fontSize: 52, fontWeight: 700, color: G.cream }}>{val}</div>
                    <button onClick={() => set(v => Math.min(max, v + 1))} style={B({ width: 48, height: 48, padding: 0, fontSize: 26, display: "flex", alignItems: "center", justifyContent: "center" })}>+</button>
                  </div>
                  <div style={{ color: G.muted, fontSize: 11, textAlign: "center", marginTop: 4 }}>{note}</div>
                </div>
              ))}

              {/* Pot amount */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ color: G.muted, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Pot Amount (USD)</div>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: G.cream, fontSize: 22, fontWeight: 700, pointerEvents: "none" }}>$</span>
                  <input
                    type="number" min="0" step="1"
                    value={pot}
                    onChange={e => setPot(e.target.value)}
                    placeholder="0"
                    style={{ width: "100%", padding: "12px 14px 12px 32px", borderRadius: 10, border: `1px solid ${G.border}`, background: "rgba(0,0,0,0.4)", color: G.cream, fontSize: 22, fontFamily: "'Georgia',serif", outline: "none", boxSizing: "border-box", fontWeight: 700, textAlign: "center" }}
                  />
                </div>
                <div style={{ color: G.muted, fontSize: 11, textAlign: "center", marginTop: 4 }}>
                  {pot ? `$${Number(pot).toLocaleString()} total prize` : "Optional — leave blank for no pot"}
                </div>
              </div>
              <div style={{ height: 1, background: G.border, margin: "20px 0" }} />
              <button onClick={startGame} style={B({ width: "100%", padding: "13px 0", fontSize: 15, background: `linear-gradient(135deg,${G.accent},#c1121f)`, color: "#fff", border: "none", borderRadius: 10, letterSpacing: 1 })}>
                {gameMode === "pinball" ? "🎯" : "🚂"} Start Game →
              </button>
            </div>
          </div>
        )}

        {/* ── PLAYER WAITING SCREEN — shown when no game is running yet ── */}
        {phase === "setup" && role === "player" && (
          <div style={{ padding: "40px 20px 0", textAlign: "center" }}>
            <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 16, padding: 36, maxWidth: 380, margin: "0 auto" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🚂</div>
              <h3 style={{ color: G.cream, margin: "0 0 10px", fontSize: 18 }}>Train Car #{TRAIN_CAR}</h3>
              <p style={{ color: G.muted, fontSize: 14, margin: "0 0 6px" }}>The admin hasn't set up the game yet.</p>
              <p style={{ color: G.muted, fontSize: 13, margin: "0 0 24px" }}>Hang tight — the train is almost ready to leave the station!</p>
              <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 24 }}>
                {[0,1,2].map(i => (
                  <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: G.accent, opacity: 0.6, animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite` }} />
                ))}
              </div>
              <button onClick={switchRole} style={B({ fontSize: 12, color: G.muted, padding: "8px 20px" })}>← Switch Role</button>
            </div>
          </div>
        )}

        {/* ── ADMIN LOGIN GATE — shown when admin not yet authed and game is in setup ── */}
        {phase === "setup" && role === "admin" && !adminAuthed && (
          <div style={{ padding: "40px 20px 0" }}>
            <div style={{ maxWidth: 360, margin: "0 auto", background: G.card, border: `1px solid ${G.border}`, borderRadius: 16, padding: 32, textAlign: "center" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🔐</div>
              <h3 style={{ color: G.cream, margin: "0 0 6px", fontSize: 18 }}>Admin Access</h3>
              <p style={{ color: G.muted, fontSize: 13, margin: "0 0 20px" }}>Enter the admin password to set up the game.</p>
              <input type="password" value={adminPassInput}
                onChange={e => { setAdminPassInput(e.target.value); setAdminPassErr(""); }}
                onKeyDown={e => e.key === "Enter" && handleAdminLogin()}
                placeholder="Password…"
                style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: `1px solid ${G.border}`, background: "rgba(0,0,0,0.4)", color: "#fff", fontSize: 15, fontFamily: "'Georgia',serif", outline: "none", boxSizing: "border-box" }}
              />
              {adminPassErr && <p style={{ color: "#f87171", fontSize: 12, margin: "8px 0 0" }}>{adminPassErr}</p>}
              <button onClick={handleAdminLogin} style={B({ width: "100%", marginTop: 14, padding: "11px 0", fontSize: 14, background: `linear-gradient(135deg,${G.accent},#c1121f)`, color: "#fff", border: "none" })}>
                Login →
              </button>
              <div style={{ marginTop: 16 }}>
                <button onClick={switchRole} style={B({ fontSize: 12, color: G.muted, padding: "8px 20px" })}>← Switch Role</button>
              </div>
            </div>
          </div>
        )}

        {/* ── PLAYING / ENDED ── */}
        {(phase === "playing" || phase === "ended") && (
          <div style={{ padding: "0 14px" }}>
            {phase === "ended" && (
              <div style={{ background: "rgba(230,57,70,0.1)", border: "1px solid rgba(230,57,70,0.35)", borderRadius: 12, padding: "12px 18px", marginBottom: 14, textAlign: "center" }}>
                <span style={{ color: G.accentLt, fontWeight: 700 }}>🏁 Game Over — </span>
                <span style={{ color: G.muted, fontSize: 13 }}>Final hands shown below</span>
                {highIds.length > 0 && (
                  <div style={{ marginTop: 6, color: G.accent, fontWeight: 700, fontSize: 15 }}>
                    👑 Winner{highIds.length > 1 ? "s" : ""}: {highIds.map(id => players[id]?.name).join(" & ")} — {scoreToLabel(getBestScore(players[highIds[0]]?.hand || []), mode)}
                    {gamePot > 0 && <span style={{ color: "#4ade80", marginLeft: 10 }}>💰 ${gamePot.toLocaleString()}</span>}
                  </div>
                )}
              </div>
            )}

            {/* Tabs */}
            <div style={{ display: "flex", marginBottom: 18, background: "rgba(0,0,0,0.45)", borderRadius: 12, padding: 3, border: `1px solid ${G.border}`, overflowX: "auto" }}>
              {[
                // Admin sees admin tab + join tab + all players
                ...(role === "admin" ? [
                  { id: "admin", label: `⚙️ Admin${pend.length > 0 ? ` (${pend.length})` : ""}` },
                  { id: "join",  label: "➕ Join" },
                  ...pl.map(p => ({ id: p.id, label: `${highIds.includes(p.id) ? "👑 " : (mode === "pinball" ? "🎯 " : "🚂 ")}${p.name}` })),
                ] : [
                  // Players only see join tab + their own tab once approved
                  { id: "join", label: "➕ Join" },
                  ...pl.filter(p => p.id === myPlayerId)
                    .map(p => ({ id: p.id, label: `${highIds.includes(p.id) ? "👑 " : (mode === "pinball" ? "🎯 " : "🚂 ")}${p.name}` })),
                ]),
              ].map(t => (
                <button key={t.id} onClick={() => setView(t.id)} style={{
                  flexShrink: 0, flex: 1, minWidth: 0, padding: "8px 10px", border: "none", borderRadius: 9, cursor: "pointer",
                  background: view === t.id ? "linear-gradient(135deg,rgba(230,57,70,0.22),rgba(230,57,70,0.08))" : "transparent",
                  color: view === t.id ? G.cream : G.muted,
                  fontWeight: view === t.id ? 700 : 400,
                  fontSize: Math.max(10, 13 - Math.floor(pl.length / 3)),
                  fontFamily: "'Georgia',serif",
                  borderBottom: view === t.id ? `2px solid ${G.accent}` : "2px solid transparent",
                  transition: "all 0.15s", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>{t.label}</button>
              ))}
            </div>

            {/* ── ADMIN TAB ── */}
            {view === "admin" && !adminAuthed && (
              <div style={{ maxWidth: 360, margin: "0 auto" }}>
                <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 16, padding: 32, textAlign: "center" }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>🔐</div>
                  <h3 style={{ color: G.cream, margin: "0 0 6px", fontSize: 18 }}>Admin Access</h3>
                  <p style={{ color: G.muted, fontSize: 13, margin: "0 0 20px" }}>Enter the admin password to continue.</p>
                  <input
                    type="password"
                    value={adminPassInput}
                    onChange={e => { setAdminPassInput(e.target.value); setAdminPassErr(""); }}
                    onKeyDown={e => e.key === "Enter" && handleAdminLogin()}
                    placeholder="Password…"
                    style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: `1px solid ${G.border}`, background: "rgba(0,0,0,0.4)", color: "#fff", fontSize: 15, fontFamily: "'Georgia',serif", outline: "none", boxSizing: "border-box" }}
                  />
                  {adminPassErr && <p style={{ color: "#f87171", fontSize: 12, margin: "8px 0 0" }}>{adminPassErr}</p>}
                  <button onClick={handleAdminLogin} style={B({ width: "100%", marginTop: 14, padding: "11px 0", fontSize: 14, background: `linear-gradient(135deg,${G.accent},#c1121f)`, color: "#fff", border: "none" })}>
                    Login →
                  </button>
                </div>
              </div>
            )}
            {view === "admin" && adminAuthed && (
              <div>
                {pend.length > 0 && (
                  <div style={{ background: "rgba(250,204,21,0.07)", border: "1px solid rgba(250,204,21,0.28)", borderRadius: 12, padding: 16, marginBottom: 14 }}>
                    <div style={{ color: "#facc15", fontSize: 12, letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>⏳ Awaiting Approval</div>
                    {pend.map(p => (
                      <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid rgba(250,204,21,0.1)" }}>
                        <span style={{ color: "#fef08a", fontWeight: 600 }}>{p.name}</span>
                        <div style={{ display: "flex", gap: 7 }}>
                          <button onClick={() => approve(p.id)} style={B({ padding: "4px 12px", fontSize: 11, background: "rgba(22,163,74,0.18)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.3)" })}>✓ Allow</button>
                          <button onClick={() => deny(p.id)}    style={B({ padding: "4px 12px", fontSize: 11, background: "rgba(220,38,38,0.18)", color: "#f87171", border: "1px solid rgba(248,113,113,0.3)" })}>✗ Deny</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: 16, marginBottom: 14 }}>
                  <div style={{ color: G.cream, fontSize: 12, letterSpacing: 1, textTransform: "uppercase", marginBottom: 14 }}>👥 All Players ({pl.length})</div>
                  {pl.length === 0
                    ? <p style={{ color: G.muted, textAlign: "center", margin: "18px 0", fontSize: 14 }}>No players yet</p>
                    : pl.map(p => {
                        const isHigh    = highIds.includes(p.id);
                        const handLabel = evalHand(p.hand, mode);
                        return (
                          <div key={p.id} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${G.border}`, borderRadius: isHigh ? 8 : 0, background: isHigh ? "rgba(230,57,70,0.07)" : "transparent", padding: isHigh ? "10px 10px 14px" : "0 0 14px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                {isHigh && <span style={{ fontSize: 15 }}>👑</span>}
                                <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>{p.name}</span>
                                <span style={{ color: G.muted, fontSize: 11 }}>{p.drawn || 0}/{MAX} drawn</span>
                                {handLabel && <span style={{ color: isHigh ? G.accent : "#a78bfa", fontSize: 11, background: isHigh ? "rgba(230,57,70,0.18)" : "rgba(167,139,250,0.12)", padding: "2px 8px", borderRadius: 20, fontWeight: isHigh ? 700 : 400 }}>{handLabel}</span>}
                              </div>
                              <div style={{ display: "flex", gap: 6 }}>
                                {phase === "playing" && (
                                  <button onClick={() => drawCard(p.id)} disabled={(p.drawn || 0) >= MAX || deck.length === 0}
                                    style={B({ padding: "4px 11px", fontSize: 11, opacity: (p.drawn || 0) >= MAX || deck.length === 0 ? 0.35 : 1, cursor: (p.drawn || 0) >= MAX || deck.length === 0 ? "not-allowed" : "pointer" })}>
                                    Draw
                                  </button>
                                )}
                                <button onClick={() => boot(p.id)} style={B({ padding: "4px 11px", fontSize: 11, color: "#f87171", border: "1px solid rgba(248,113,113,0.3)", background: "rgba(220,38,38,0.1)" })}>Boot</button>
                              </div>
                            </div>
                            {(p.hand || []).length > 0
                              ? <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                                  {(p.hand || []).map(c => (
                                    <div key={c.id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                                      <Card card={c} sm />
                                      {phase === "playing" && (
                                        <button onClick={() => promptReturn(p.id, c.id)} title="Return card to deck"
                                          style={{ width: 50, padding: "2px 0", borderRadius: 4, border: "1px solid rgba(230,57,70,0.4)", background: "rgba(230,57,70,0.12)", color: "#e63946", fontSize: 11, cursor: "pointer", fontWeight: 700 }}>↩</button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              : <span style={{ color: G.muted, fontSize: 12 }}>No cards yet</span>
                            }
                          </div>
                        );
                      })
                  }
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  {phase === "playing" && !gameLocked && (
                    <button onClick={lockGame}
                      disabled={pend.length > 0 || pl.length === 0}
                      title={pl.length === 0 ? "At least one player must join first" : pend.length > 0 ? "Approve or deny all pending players first" : "Lock — no new players can join"}
                      style={B({ background: (pend.length > 0 || pl.length === 0) ? "rgba(255,255,255,0.04)" : "rgba(22,163,74,0.18)", color: (pend.length > 0 || pl.length === 0) ? G.muted : "#4ade80", border: `1px solid ${(pend.length > 0 || pl.length === 0) ? "rgba(255,255,255,0.1)" : "rgba(74,222,128,0.35)"}`, cursor: (pend.length > 0 || pl.length === 0) ? "not-allowed" : "pointer", opacity: (pend.length > 0 || pl.length === 0) ? 0.5 : 1 })}>
                      🔒 Start Game
                    </button>
                  )}
                  {phase === "playing" && gameLocked && (
                    <span style={{ color: "#4ade80", fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>🔒 Game Locked</span>
                  )}
                  {phase === "playing" && (
                    <button onClick={endGame} style={B({ background: "rgba(220,38,38,0.25)", color: "#f87171", border: "1px solid rgba(248,113,113,0.5)", fontSize: 14, padding: "10px 22px" })}>🏁 End Game</button>
                  )}
                  {/* New Game — Same Players: only available after a game has been completed */}
                  {phase === "ended" && pl.length > 0 && (
                    <button onClick={rematchGame}
                      style={B({ background: "linear-gradient(135deg,rgba(22,163,74,0.3),rgba(22,163,74,0.15))", color: "#4ade80", border: "1px solid rgba(74,222,128,0.4)", fontSize: 13, padding: "10px 16px" })}>
                      🔄 New Game — Same Players
                    </button>
                  )}
                  {/* New Game: full reset, clears everything, back to Setup */}
                  <button onClick={reset} style={B({ fontSize: 13, padding: "10px 16px" })}>🗑️ New Game</button>
                  {/* Mid-game pot editor */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(0,0,0,0.3)", border: `1px solid ${G.border}`, borderRadius: 8, padding: "4px 10px" }}>
                    <span style={{ color: "#4ade80", fontSize: 14, fontWeight: 700 }}>$</span>
                    <input
                      type="number" min="0" step="1"
                      value={gamePot || ""}
                      onChange={e => fbSet("pot", e.target.value ? Number(e.target.value) : 0)}
                      placeholder="Pot"
                      style={{ width: 64, background: "transparent", border: "none", color: "#4ade80", fontSize: 14, fontWeight: 700, outline: "none", fontFamily: "'Georgia',serif" }}
                    />
                  </div>
                  <span style={{ color: G.muted, fontSize: 12, marginLeft: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                    <span>{deck.length} cards remain</span>
                    <span style={{ color: "rgba(241,227,198,0.25)", fontSize: 11 }}>Train Car #{TRAIN_CAR}</span>
                  </span>
                </div>
              </div>
            )}

            {/* ── JOIN TAB ── */}
            {view === "join" && (
              <div style={{ maxWidth: 380, margin: "0 auto" }}>
                <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 16, padding: 28 }}>
                  {/* Prominent Train Car banner */}
                  <div style={{ textAlign: "center", marginBottom: 22 }}>
                    <div style={{ fontSize: 11, letterSpacing: 4, color: G.muted, textTransform: "uppercase", marginBottom: 6 }}>You are joining</div>
                    <div style={{ display: "inline-block", background: `linear-gradient(135deg,${G.accent},#c1121f)`, borderRadius: 12, padding: "10px 24px", marginBottom: 4 }}>
                      <span style={{ color: "#fff", fontSize: 22, fontWeight: 800, fontStyle: "italic", letterSpacing: 1 }}>
                        🚂 Gravy Train Car #{TRAIN_CAR}
                      </span>
                    </div>
                    <div style={{ width: 140, height: 1, background: `linear-gradient(90deg,transparent,${G.accent},transparent)`, margin: "12px auto 0" }} />
                  </div>

                  {gameLocked ? (
                    <div style={{ textAlign: "center", padding: "16px 0" }}>
                      <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
                      <p style={{ color: "#f87171", fontWeight: 700, fontSize: 15, margin: "0 0 8px" }}>Train Car #{TRAIN_CAR} is locked</p>
                      <p style={{ color: G.muted, fontSize: 13, margin: 0 }}>The admin has started the game. No new players can join.</p>
                    </div>
                  ) : (
                    <>
                      <input value={joinName} onChange={e => { setJoinName(e.target.value); setJoinErr(""); }}
                        onKeyDown={e => e.key === "Enter" && reqJoin()}
                        placeholder="Your name…"
                        style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: `1px solid ${G.border}`, background: "rgba(0,0,0,0.4)", color: "#fff", fontSize: 15, fontFamily: "'Georgia',serif", outline: "none", boxSizing: "border-box" }}
                      />
                      {joinErr && <p style={{ color: "#f87171", fontSize: 12, margin: "6px 0 0" }}>{joinErr}</p>}
                      <button onClick={reqJoin} style={B({ width: "100%", marginTop: 13, padding: "11px 0", fontSize: 14, background: `linear-gradient(135deg,${G.accent},#c1121f)`, color: "#fff", border: "none" })}>
                        Board Gravy Train Car #{TRAIN_CAR} →
                      </button>
                      <p style={{ color: G.muted, fontSize: 12, textAlign: "center", margin: "12px 0 0" }}>
                        Admin must approve before you can draw cards.
                      </p>
                    </>
                  )}
                  {/* Pending approval message */}
                  {myPlayerId && pending[myPlayerId] && (
                    <div style={{ textAlign: "center", padding: "20px 0 8px" }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
                      <p style={{ color: G.cream, fontWeight: 700, margin: "0 0 4px" }}>Waiting for approval…</p>
                      <p style={{ color: G.muted, fontSize: 12, margin: 0 }}>The admin will let you in shortly.</p>
                    </div>
                  )}
                  <div style={{ height: 1, background: G.border, margin: "20px 0 14px" }} />
                  <button onClick={switchRole} style={B({ width: "100%", padding: "9px 0", fontSize: 12, color: G.muted })}>
                    ← Switch Role
                  </button>
                </div>
              </div>
            )}

            {/* ── PLAYER TAB ── */}
            {view !== "admin" && view !== "join" && (() => {
              const p = players[view];
              if (!p) return null;
              const hand      = p.hand || [];
              const drawn     = p.drawn || 0;
              const canDraw   = phase === "playing" && drawn < MAX && deck.length > 0;
              const isHigh    = highIds.includes(p.id);
              const handLabel = evalHand(hand, mode);
              return (
                <div>
                  {/* ── GAME OVER: show all hands read-only ── */}
                  {phase === "ended" && (
                    <div>
                      <div style={{ background: "rgba(230,57,70,0.08)", border: "1px solid rgba(230,57,70,0.25)", borderRadius: 12, padding: "14px 20px", marginBottom: 18, textAlign: "center" }}>
                        <div style={{ color: G.accentLt, fontWeight: 700, fontSize: 16, marginBottom: 4 }}>🏁 Game Over</div>
                        {highIds.length > 0 && (
                          <div style={{ color: G.accent, fontWeight: 700, fontSize: 15 }}>
                            👑 Winner{highIds.length > 1 ? "s" : ""}: {highIds.map(id => players[id]?.name).join(" & ")} — {scoreToLabel(getBestScore(players[highIds[0]]?.hand || []), mode)}
                            {gamePot > 0 && <div style={{ color: "#4ade80", fontSize: 20, fontWeight: 800, marginTop: 6 }}>💰 ${gamePot.toLocaleString()}</div>}
                          </div>
                        )}
                      </div>
                      <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: 16 }}>
                        <div style={{ color: G.cream, fontSize: 12, letterSpacing: 1, textTransform: "uppercase", marginBottom: 14 }}>👥 Final Hands</div>
                        {pl.map(fp => {
                          const fpHigh  = highIds.includes(fp.id);
                          const fpLabel = evalHand(fp.hand || [], mode);
                          const isMe    = fp.id === myPlayerId;
                          return (
                            <div key={fp.id} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${G.border}`, borderRadius: fpHigh ? 8 : 0, background: fpHigh ? "rgba(230,57,70,0.07)" : "transparent", padding: fpHigh ? "10px 10px 14px" : "0 0 14px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                                {fpHigh && <span>👑</span>}
                                <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
                                  {fp.name}{isMe && <span style={{ color: G.muted, fontSize: 11, fontWeight: 400, marginLeft: 6 }}>(you)</span>}
                                </span>
                                <span style={{ color: G.muted, fontSize: 11 }}>{fp.drawn || 0} cards drawn</span>
                                {fpLabel && (
                                  <span style={{ color: fpHigh ? G.accent : "#a78bfa", fontSize: 11, background: fpHigh ? "rgba(230,57,70,0.18)" : "rgba(167,139,250,0.12)", padding: "2px 8px", borderRadius: 20, fontWeight: fpHigh ? 700 : 400 }}>
                                    {fpLabel}
                                  </span>
                                )}
                              </div>
                              {(fp.hand || []).length > 0
                                ? <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{(fp.hand || []).map(c => <Card key={c.id} card={c} sm />)}</div>
                                : <span style={{ color: G.muted, fontSize: 12 }}>No cards drawn</span>
                              }
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* ── PLAYING: show own hand + draw button ── */}
                  {phase === "playing" && (() => {
                    const now          = Date.now();
                    const lastDraw     = p.lastDrawAt || 0;
                    const cooldownLeft = mode === "pinball" ? 0 : Math.max(0, COOLDOWN_MS - (now - lastDraw));
                    const onCooldown   = cooldownLeft > 0;
                    const secs         = Math.ceil(cooldownLeft / 1000);
                    const canDrawNow   = canDraw && !onCooldown;
                    return (
                      <div>
                        {handLabel && (
                          <div style={{ background: isHigh ? "linear-gradient(135deg,rgba(230,57,70,0.2),rgba(230,57,70,0.06))" : "linear-gradient(135deg,rgba(241,227,198,0.08),rgba(241,227,198,0.02))", border: `1px solid ${isHigh ? G.accent : G.border}`, borderRadius: 12, padding: "14px 20px", marginBottom: 14, textAlign: "center" }}>
                            {isHigh && <div style={{ color: G.accent, fontSize: 11, letterSpacing: 3, textTransform: "uppercase", marginBottom: 4 }}>👑 HIGH HAND</div>}
                            <div style={{ color: G.muted, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", marginBottom: 3 }}>Best Hand</div>
                            <div style={{ color: isHigh ? G.accent : G.cream, fontSize: 20, fontWeight: 700 }}>{handLabel}</div>
                          </div>
                        )}

                        {/* Long-press draw button */}
                        <div style={{ textAlign: "center", marginBottom: 22 }}>
                          <LongPressButton
                            onTrigger={() => drawCard(p.id)}
                            disabled={!canDrawNow}
                            style={{ display: "inline-block" }}
                          >
                            <div style={{ padding: "16px 44px", background: canDrawNow ? `linear-gradient(135deg,${G.accent},#c1121f)` : "rgba(255,255,255,0.05)", color: canDrawNow ? "#fff" : G.muted, fontWeight: 800, fontSize: 17, fontFamily: "'Georgia',serif", letterSpacing: 1, boxShadow: canDrawNow ? "0 0 28px rgba(230,57,70,0.35)" : "none", opacity: canDrawNow ? 1 : 0.5, borderRadius: 14, transition: "background 0.2s, opacity 0.2s" }}>
                              {drawn >= MAX ? "Max Cards Reached" : deck.length === 0 ? "Deck Empty" : onCooldown ? `⏳ Wait ${secs}s…` : (mode === "pinball" ? "🎯 Hold to Draw" : "🚂 Hold to Draw")}
                            </div>
                          </LongPressButton>
                          <div style={{ color: G.muted, fontSize: 12, marginTop: 7 }}>
                            {onCooldown
                              ? <span style={{ color: G.accent }}>Next draw available in {secs}s</span>
                              : `${MAX - drawn} draw${MAX - drawn !== 1 ? "s" : ""} remaining — hold the button to draw`
                            }
                          </div>
                        </div>

                        {/* Your hand */}
                        <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: 18, marginBottom: 16 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                            <div style={{ color: G.cream, fontSize: 12, letterSpacing: 1, textTransform: "uppercase" }}>Your Hand</div>
                            <span style={{ color: G.muted, fontSize: 12 }}>{hand.length} card{hand.length !== 1 ? "s" : ""}</span>
                          </div>
                          {hand.length === 0
                            ? <div style={{ textAlign: "center", padding: "28px 0", color: G.muted }}><div style={{ fontSize: 44, marginBottom: 8, opacity: 0.35 }}>{mode === "pinball" ? "🎯" : "🚂"}</div><p style={{ margin: 0, fontSize: 13 }}>{mode === "pinball" ? "No cards yet — hold to draw!" : "No cards yet — hold the button to draw!"}</p></div>
                            : <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>{hand.map(c => <Card key={c.id} card={c} />)}</div>
                          }
                        </div>

                        {/* Other players' hands — read only */}
                        {pl.filter(op => op.id !== p.id).length > 0 && (
                          <div style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                            <div style={{ color: G.cream, fontSize: 12, letterSpacing: 1, textTransform: "uppercase", marginBottom: 14 }}>👥 Other Players</div>
                            {pl.filter(op => op.id !== p.id).map(op => {
                              const opHigh  = highIds.includes(op.id);
                              const opLabel = evalHand(op.hand || [], mode);
                              return (
                                <div key={op.id} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${G.border}`, borderRadius: opHigh ? 8 : 0, background: opHigh ? "rgba(230,57,70,0.06)" : "transparent", padding: opHigh ? "8px 10px 12px" : "0 0 12px" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                                    {opHigh && <span>👑</span>}
                                    <span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>{op.name}</span>
                                    <span style={{ color: G.muted, fontSize: 11 }}>{op.drawn || 0} drawn</span>
                                    {opLabel && <span style={{ color: opHigh ? G.accent : "#a78bfa", fontSize: 11, background: opHigh ? "rgba(230,57,70,0.18)" : "rgba(167,139,250,0.12)", padding: "2px 8px", borderRadius: 20 }}>{opLabel}</span>}
                                  </div>
                                  {(op.hand || []).length > 0
                                    ? <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>{(op.hand || []).map(c => <Card key={c.id} card={c} sm />)}</div>
                                    : <span style={{ color: G.muted, fontSize: 12 }}>No cards yet</span>
                                  }
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })()}
          </div>
        )}


      </div>

      {/* ── FIXED CHAT BAR — top of screen during playing/ended ── */}
      {(phase === "playing" || phase === "ended") && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 150,
          background: "rgba(10,20,34,0.97)",
          borderBottom: `1px solid ${G.border}`,
          backdropFilter: "blur(8px)",
          boxShadow: "0 2px 20px rgba(0,0,0,0.5)",
        }}>
          {/* Toggle bar — always visible */}
          <div
            onClick={() => {
              setChatOpen(prev => {
                const next = !prev;
                if (next) {
                  // Opening — clear unread, scroll to bottom
                  setUnreadCount(0);
                  seenCountRef.current = chatMessages.length;
                  setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
                }
                return next;
              });
            }}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", cursor: "pointer", userSelect: "none" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 16 }}>💬</span>
              <span style={{ color: G.cream, fontSize: 13, fontWeight: 700, letterSpacing: 0.5 }}>Chat</span>
              <span style={{ color: G.muted, fontSize: 11 }}>— visible to everyone</span>
              {/* Unread badge */}
              {!chatOpen && unreadCount > 0 && (
                <div style={{
                  background: G.accent, color: "#fff", borderRadius: 20,
                  fontSize: 11, fontWeight: 800, padding: "2px 7px",
                  minWidth: 20, textAlign: "center",
                  animation: "badgePop 0.3s ease",
                }}>
                  {unreadCount > 99 ? "99+" : unreadCount}
                </div>
              )}
              {/* Dot indicator when closed and has messages */}
              {!chatOpen && unreadCount === 0 && chatMessages.length > 0 && (
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: G.muted }} />
              )}
            </div>
            <div style={{ color: G.muted, fontSize: 18, lineHeight: 1, transition: "transform 0.2s", transform: chatOpen ? "rotate(180deg)" : "none" }}>
              ▾
            </div>
          </div>

          {/* Collapsible body */}
          {chatOpen && (
            <div>
              {/* Messages */}
              <div style={{ height: 260, overflowY: "auto", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12, borderTop: `1px solid ${G.border}` }}>
                {chatMessages.length === 0
                  ? <p style={{ color: G.muted, fontSize: 15, margin: "auto", textAlign: "center" }}>No messages yet — say something!</p>
                  : chatMessages.map((msg, i) => {
                      const myName = role === "admin" ? "Admin 🔐" : (players[myPlayerId]?.name || "");
                      const isMe   = msg.name === myName;
                      return (
                        <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: isMe ? "flex-end" : "flex-start" }}>
                          <span style={{ color: G.muted, fontSize: 12, marginBottom: 4 }}>{msg.name}</span>
                          <div style={{
                            background: isMe ? "rgba(230,57,70,0.22)" : "rgba(255,255,255,0.07)",
                            border: `1px solid ${isMe ? "rgba(230,57,70,0.4)" : G.border}`,
                            borderRadius: 12, padding: "10px 16px", maxWidth: "80%",
                          }}>
                            <span style={{ color: G.cream, fontSize: 16, lineHeight: 1.4 }}>{msg.text}</span>
                          </div>
                        </div>
                      );
                    })
                }
                <div ref={chatBottomRef} />
              </div>
              {/* Emoji picker panel */}
              {emojiOpen && (
                <div style={{ borderTop: `1px solid ${G.border}`, background: "rgba(0,0,0,0.5)" }}>
                  {/* Category tabs */}
                  <div style={{ display: "flex", overflowX: "auto", borderBottom: `1px solid ${G.border}`, padding: "0 4px" }}>
                    {(mode === "pinball" ? EMOJI_CATEGORIES_PINBALL : EMOJI_CATEGORIES_BOWLING).map((cat, i) => (
                      <button key={i} onClick={() => setEmojiCat(i)}
                        style={{ flexShrink: 0, padding: "10px 12px", border: "none", background: "transparent", cursor: "pointer", fontSize: 13, color: emojiCat === i ? G.cream : G.muted, borderBottom: emojiCat === i ? `2px solid ${G.accent}` : "2px solid transparent", fontFamily: "'Georgia',serif", whiteSpace: "nowrap" }}>
                        {cat.label}
                      </button>
                    ))}
                  </div>
                  {/* Emoji grid */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "12px 14px", maxHeight: 150, overflowY: "auto" }}>
                    {(mode === "pinball" ? EMOJI_CATEGORIES_PINBALL : EMOJI_CATEGORIES_BOWLING)[emojiCat].emojis.map((em, i) => (
                      <button key={i} onClick={() => insertEmoji(em)}
                        style={{ width: 46, height: 46, border: `1px solid ${G.border}`, borderRadius: 8, background: "rgba(255,255,255,0.05)", cursor: "pointer", fontSize: 22, display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.1s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(230,57,70,0.2)"}
                        onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}>
                        {em}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Input row */}
              <div style={{ padding: "10px 12px", borderTop: `1px solid ${G.border}`, display: "flex", gap: 10, alignItems: "center" }}>
                <button onClick={() => setEmojiOpen(prev => !prev)} title="Emoji picker"
                  style={{ flexShrink: 0, width: 44, height: 44, border: `1px solid ${emojiOpen ? G.accent : G.border}`, borderRadius: 10, background: emojiOpen ? "rgba(230,57,70,0.15)" : "rgba(255,255,255,0.05)", cursor: "pointer", fontSize: 22, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>
                  😊
                </button>
                <input
                  ref={chatInputRef}
                  value={chatMsg}
                  onChange={e => setChatMsg(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && sendChat()}
                  placeholder="Type a message…"
                  style={{ flex: 1, padding: "12px 14px", borderRadius: 10, border: `1px solid ${G.border}`, background: "rgba(0,0,0,0.4)", color: "#fff", fontSize: 16, fontFamily: "'Georgia',serif", outline: "none" }}
                />
                <button onClick={sendChat} style={B({ flexShrink: 0, padding: "12px 20px", fontSize: 15, background: `linear-gradient(135deg,${G.accent},#c1121f)`, color: "#fff", border: "none" })}>
                  Send
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Log Out Everyone Confirmation Modal ── */}
      {confirmLogoutAll && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#0d1b2a", border: `1px solid #f87171`, borderRadius: 16, padding: 28, maxWidth: 380, width: "100%", textAlign: "center", boxShadow: "0 8px 40px rgba(0,0,0,0.6)" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔴</div>
            <h3 style={{ color: G.cream, margin: "0 0 10px", fontSize: 18 }}>Log Out Everyone?</h3>
            <p style={{ color: G.muted, fontSize: 14, margin: "0 0 8px" }}>
              This will immediately end all active sessions — every player and the admin will be sent back to the role selection screen.
            </p>
            <p style={{ color: "#f87171", fontSize: 13, margin: "0 0 24px", fontStyle: "italic" }}>
              Use this between weekly sessions to clear out last week's players.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setConfirmLogoutAll(false)} style={B({ flex: 1, padding: "11px 0", fontSize: 14 })}>Cancel</button>
              <button onClick={logoutAll} style={B({ flex: 1, padding: "11px 0", fontSize: 14, background: "linear-gradient(135deg,#dc2626,#991b1b)", color: "#fff", border: "none" })}>
                Yes, Log Everyone Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Return Card Confirmation Modal ── */}
      {confirmReturn && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#0d1b2a", border: `1px solid ${G.accent}`, borderRadius: 16, padding: 28, maxWidth: 360, width: "100%", textAlign: "center", boxShadow: "0 8px 40px rgba(0,0,0,0.6)" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>↩</div>
            <h3 style={{ color: G.cream, margin: "0 0 10px", fontSize: 17 }}>Return Card?</h3>
            <p style={{ color: G.muted, margin: "0 0 6px", fontSize: 14 }}>Return <strong style={{ color: G.cream }}>{confirmReturn.label}</strong> from</p>
            <p style={{ color: G.accent, fontWeight: 700, fontSize: 16, margin: "0 0 20px" }}>{confirmReturn.playerName}'s hand</p>
            <p style={{ color: G.muted, fontSize: 12, margin: "0 0 24px" }}>The card will be shuffled back into the deck.</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setConfirmReturn(null)} style={B({ flex: 1, padding: "11px 0", fontSize: 14 })}>Cancel</button>
              <button onClick={confirmReturnCard} style={B({ flex: 1, padding: "11px 0", fontSize: 14, background: `linear-gradient(135deg,${G.accent},#c1121f)`, color: "#fff", border: "none" })}>Yes, Return It</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fi { from { opacity:0; transform:translateX(-50%) translateY(-8px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }
        @keyframes pulse { 0%,100%{opacity:0.3;transform:scale(0.8)} 50%{opacity:1;transform:scale(1.2)} }
        @keyframes badgePop { 0%{transform:scale(0.5);opacity:0} 70%{transform:scale(1.2)} 100%{transform:scale(1);opacity:1} }
        * { box-sizing: border-box; }
        input::placeholder { color: #6b7280; }
      `}</style>
    </div>
  );
}
